import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import {
  getBuilderSteps,
  type BuilderStep,
  type BuilderView,
} from "@/client/constants/builder-steps";
import { isStepComplete } from "@/client/pages/client/builder/_components/sidebar/step-status";
import { useResumeStore } from "@/client/stores/resume";

type StepFormPhase = "intro" | "form";

type StepFormContextValue = {
  selector: BuilderView;
  phase: StepFormPhase;
  steps: BuilderView[];
  setSelector: (step: BuilderView) => void;
  showForm: () => void;
  nextStep: () => void;
  prevStep: () => void;
};

const StepFormContext = createContext<StepFormContextValue | null>(null);

export const StepFormProvider = ({ children }: { children: ReactNode }) => {
  const resume = useResumeStore((state) => state.resume);

  const [selector, setCurrentSelector] = useState<BuilderView>("basics");

  const [phase, setPhase] = useState<StepFormPhase>("form");

  const steps = useMemo<BuilderView[]>(
    () => getBuilderSteps(resume.data.metadata.layout, resume.data.sections),
    [resume.data.metadata.layout, resume.data.sections],
  );

  /*
   * Détermine si une rubrique
   * possède déjà du contenu.
   *
   * Les vues techniques comme
   * "templates" ne sont pas des
   * BuilderStep et sont donc
   * exclues de cette vérification.
   */
  const stepHasContent = useCallback(
    (step: BuilderView): boolean => {
      if (step === "templates" || step === "finalization") {
        return false;
      }

      return isStepComplete(step as BuilderStep, resume);
    },
    [resume],
  );

  /*
   * Une rubrique déjà remplie
   * s’ouvre directement sur son
   * formulaire ou sa liste.
   *
   * Une rubrique vide conserve
   * son écran pédagogique
   * d’introduction.
   */
  const selectStep = useCallback(
    (step: BuilderView) => {
      setCurrentSelector(step);

      const directForm =
        step === "basics" ||
        step === "templates" ||
        step === "finalization" ||
        !steps.includes(step) ||
        stepHasContent(step);

      setPhase(directForm ? "form" : "intro");
    },
    [stepHasContent, steps],
  );

  const showForm = useCallback(() => {
    setPhase("form");
  }, []);

  const nextStep = useCallback(() => {
    if (phase === "intro") {
      setPhase("form");
      return;
    }

    const index = steps.indexOf(selector);

    if (index < 0 || index >= steps.length - 1) {
      return;
    }

    selectStep(steps[index + 1]);
  }, [phase, selectStep, selector, steps]);

  const prevStep = useCallback(() => {
    const index = steps.indexOf(selector);

    if (index <= 0) {
      return;
    }

    selectStep(steps[index - 1]);
  }, [selectStep, selector, steps]);

  const value = useMemo<StepFormContextValue>(
    () => ({
      selector,
      phase,
      steps,
      setSelector: selectStep,
      showForm,
      nextStep,
      prevStep,
    }),
    [selector, phase, steps, selectStep, showForm, nextStep, prevStep],
  );

  return <StepFormContext.Provider value={value}>{children}</StepFormContext.Provider>;
};

export const useStepFormResume = () => {
  const context = useContext(StepFormContext);

  if (!context) {
    throw new Error("useStepFormResume must be used within StepFormProvider");
  }

  return context;
};
